import { DOCUMENT, Location } from '@angular/common';
import { Inject, Injectable, Injector } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import type { CraftXGritProductEntry } from '@core-mkt/interfaces/craft-xgrit-product-entry';
import { CookieService } from '@core-mkt/services/cookie/cookie.service';
import { QueryParamService } from '@core-mkt/services/query-param/query-param.service';
import type { LogError } from '@core-mkt/shared/lib/logger';
import { logError } from '@core-mkt/shared/lib/logger';
import { GlobalCouponOverrideService } from '@core-mkt/shared/services/global-coupon-override/global-coupon-override.service';

/**
 * Interface for the comprehensive coupon collection request
 * that will be sent to the API for resolution
 */
export interface CouponCollectionRequest {
  /** Current page URL for context-aware coupon application */
  url: string;
  /** All potential coupons from various sources */
  coupons: {
    /** Coupons attached to Craft CMS product entries */
    craft?: string[];
    /** Coupons from Bundle Builder and Product Builder categories */
    builderCategories?: string[];
    /** Coupon(s) set in Global Banner override field - supports comma-separated multiple coupons */
    globalBanner?: string[];
    /**
     * Coupon saved from the email capture form. Craft's email-capture `coupon` field accepts a
     * comma-separated list (a sale can stack several codes), so this is the raw cookie value;
     * `getAllCouponCodes` splits it into individual codes.
     */
    emailCapture?: string;
    /**
     * Coupon saved from the sticky phone capture form (separate `phoneCoupon` cookie). Same raw,
     * possibly comma-separated shape as `emailCapture`.
     */
    phoneCapture?: string;
    /** All coupon parameters parsed from URL */
    urlParameters: UrlCouponParameters;
  };
}

/**
 * All supported URL coupon parameters for the Coupon Overhaul
 */
export interface UrlCouponParameters {
  /** Legacy sales parameter */
  couponCode?: string;
  /** Voucher coupon - new sales parameter */
  vc?: string;
  /** Hold coupon - new CDI parameter */
  hc?: string;
  /** Channel-specific coupons */
  channel?: string;
  /** Cross-reference hold coupon */
  crohc?: string;
  /** Legacy CDI parameter */
  coupon?: string;
  /** Ambassador RSA coupons */
  couponAmbassadorRSA?: string;
  /** General ambassador coupons */
  couponAmbassador?: string;
  /** Short coupon parameter */
  c?: string;
}

/** The banner slots of the resolved page data that can carry an `overwriteCoupon`. */
interface BannerPageData {
  craftData?: {
    banners?: { topBanner?: { overwriteCoupon?: string }[] };
    header?: {
      aceHeaderPromoBanner?: { overwriteCoupon?: string }[];
      realEstateHeaderPromoBanner?: { overwriteCoupon?: string }[];
    };
  };
}

/**
 * Service to aggregate all potential coupons from various sources
 * for the Coupon Overhaul API-driven resolution system
 */
@Injectable({
  providedIn: 'root',
})
export class CouponAggregationService {
  /** All URL coupon parameter names to parse */
  private readonly URL_COUPON_PARAMETERS = [
    'couponCode',
    'vc',
    'hc',
    'channel',
    'crohc',
    'coupon',
    'couponAmbassadorRSA',
    'couponAmbassador',
    'c',
  ] as const;

  constructor(
    private queryParamService: QueryParamService,
    private globalCouponOverrideService: GlobalCouponOverrideService,
    private cookieService: CookieService,
    private location: Location,
    private injector: Injector,
    @Inject(DOCUMENT) private document: Document,
  ) {}

  /**
   * Collects all potential coupons from all sources for API resolution
   * @param craftProducts - Optional array of Craft product entries with potential coupons
   * @param builderCategoryCoupons - Optional array of coupons from builder categories
   * @returns Complete coupon collection request object
   */
  collectAllCoupons(
    craftProducts?: CraftXGritProductEntry[],
    builderCategoryCoupons?: string[],
  ): CouponCollectionRequest {
    const currentUrl = this.getCurrentPageUrl();

    const craftCoupons = this.extractCraftProductCoupons(craftProducts);
    const builderCoupons = this.filterValidCoupons(builderCategoryCoupons);
    const bannerCoupons = this.getGlobalBannerCoupon();
    const emailCoupon = this.getEmailCaptureCoupon();
    const phoneCoupon = this.getPhoneCaptureCoupon();
    const urlCoupons = this.parseAllUrlCouponParameters();

    const couponRequest = {
      url: currentUrl,
      coupons: {
        craft: craftCoupons,
        builderCategories: builderCoupons,
        globalBanner: bannerCoupons,
        emailCapture: emailCoupon,
        phoneCapture: phoneCoupon,
        urlParameters: urlCoupons,
      },
    };

    return couponRequest;
  }

  /**
   * Gets the current page URL for context-aware coupon application
   */
  private getCurrentPageUrl(): string {
    // Get full URL including protocol, host, and path
    const currentPath = this.location.path();
    const baseUrl = this.document.location?.origin || '';
    return `${baseUrl}${currentPath}`;
  }

  /**
   * Extracts coupon codes from Craft product entries
   */
  private extractCraftProductCoupons(craftProducts?: CraftXGritProductEntry[]): string[] | undefined {
    if (!craftProducts?.length) {
      return undefined;
    }

    const coupons = craftProducts.flatMap(
      (product) =>
        product.xgritCoupon
          ?.split(',')
          .map((coupon) => coupon.trim())
          .filter((coupon) => Boolean(coupon)) || [],
    );

    return coupons.length > 0 ? coupons : undefined;
  }

  /**
   * Gets the global banner override coupon(s) if set
   * Supports comma-separated multiple coupons
   * Checks both GlobalCouponOverrideService and route data as fallback
   */
  private getGlobalBannerCoupon(): string[] | undefined {
    // First try the GlobalCouponOverrideService (set by banner component)
    let coupon = this.globalCouponOverrideService.getCurrentOverrideCoupon();

    // If no coupon from service, try to get it directly from route data as fallback
    if (!coupon?.trim()) {
      coupon = this.getBannerCouponFromRouteData();
    }

    if (!coupon?.trim()) {
      return undefined;
    }

    const coupons = coupon
      .split(',')
      .map((c) => c.trim())
      .filter((c) => Boolean(c));

    return coupons.length > 0 ? coupons : undefined;
  }

  /**
   * Extract banner coupon directly from router state
   * This bypasses component timing issues by accessing the same resolver data
   * that components use, but at the service level
   */
  private getBannerCouponFromRouteData(): string | null {
    try {
      // Get the ActivatedRoute from injector to access current route state
      const route = this.injector.get(ActivatedRoute);

      // Check current route first
      let pageData = route.snapshot.data?.pageData as BannerPageData | undefined;

      // If not found, try firstChild (this is where pageData usually is)
      if (!pageData && route.firstChild) {
        pageData = route.firstChild.snapshot.data?.pageData as BannerPageData | undefined;
      }

      // If still not found, try root route
      if (!pageData) {
        let rootRoute = route;
        while (rootRoute.parent) {
          rootRoute = rootRoute.parent;
        }
        pageData = rootRoute.snapshot.data?.pageData as BannerPageData | undefined;
      }

      if (!pageData) {
        return null;
      }

      // Check multiple possible banner locations in the data structure
      const bannerSources = [
        pageData?.craftData?.banners?.topBanner?.[0]?.overwriteCoupon,
        pageData?.craftData?.header?.aceHeaderPromoBanner?.[0]?.overwriteCoupon,
        pageData?.craftData?.header?.realEstateHeaderPromoBanner?.[0]?.overwriteCoupon,
      ];

      for (const source of bannerSources) {
        if (source?.trim()) {
          return source;
        }
      }

      return null;
    } catch (error: unknown) {
      logError({ error: error as LogError, message: 'ROUTE_DATA_ACCESS_ERROR', tags: ['COUPON', 'BANNER'] });
      return null;
    }
  }

  /**
   * Gets the email capture coupon from cookies
   */
  private getEmailCaptureCoupon(): string | undefined {
    const coupon = this.cookieService.getCookie('emailCoupon');
    return coupon?.trim() || undefined;
  }

  /**
   * Gets the sticky phone capture coupon from cookies. Kept separate from the email
   * capture cookie so a phone submission never makes the email capture component think
   * it was submitted (which would pop its modal on other pages).
   */
  private getPhoneCaptureCoupon(): string | undefined {
    const coupon = this.cookieService.getCookie('phoneCoupon');
    return coupon?.trim() || undefined;
  }

  /**
   * Parses all supported coupon parameters from the current URL
   */
  private parseAllUrlCouponParameters(): UrlCouponParameters {
    const urlCoupons: UrlCouponParameters = {};

    for (const paramName of this.URL_COUPON_PARAMETERS) {
      const value = this.queryParamService.getQueryParam(paramName);
      if (value?.trim()) {
        urlCoupons[paramName] = value.trim();
      }
    }
    return urlCoupons;
  }

  /**
   * Splits a comma-separated coupon string into trimmed, non-empty codes.
   */
  private splitCouponList(value?: string): string[] {
    if (!value) {
      return [];
    }

    return value
      .split(',')
      .map((coupon) => coupon.trim())
      .filter((coupon) => Boolean(coupon));
  }

  /**
   * Filters out null/undefined/empty coupon codes from arrays
   */
  private filterValidCoupons(coupons?: string[]): string[] | undefined {
    if (!coupons?.length) {
      return undefined;
    }

    const validCoupons = coupons.map((coupon) => coupon?.trim()).filter((coupon): coupon is string => Boolean(coupon));
    return validCoupons.length > 0 ? validCoupons : undefined;
  }

  /**
   * Utility method to check if any coupons are present from any source
   */
  hasCoupons(couponRequest: CouponCollectionRequest): boolean {
    const { coupons } = couponRequest;

    return !!(
      coupons.craft?.length ||
      coupons.builderCategories?.length ||
      coupons.globalBanner?.length ||
      coupons.emailCapture ||
      coupons.phoneCapture ||
      Object.keys(coupons.urlParameters).length
    );
  }

  /**
   * Categorizes coupon codes by their likely type based on naming patterns
   * Used for testing and validation of coupon hierarchy
   */
  categorizeCouponsByType(allCoupons: string[]): Record<string, string[]> {
    const categories = {
      STRIKETHROUGH: [],
      OFFER: [],
      CRO: [],
      SITESALE: [],
      AFFILIATE: [],
      AMBASSADOR: [],
      CAMPAIGN: [],
      EMAILCAPTURE: [],
      COMMON: [],
      UNKNOWN: [],
    };

    allCoupons.forEach((coupon) => {
      const upperCoupon = coupon.toUpperCase();

      if (upperCoupon.includes('STRIKE') || upperCoupon.includes('CROSS')) {
        categories.STRIKETHROUGH.push(coupon);
      } else if (upperCoupon.includes('OFFER') || upperCoupon.includes('PROMO')) {
        categories.OFFER.push(coupon);
      } else if (upperCoupon.includes('CRO') || upperCoupon.includes('CONVERT')) {
        categories.CRO.push(coupon);
      } else if (upperCoupon.includes('SALE') || upperCoupon.includes('DISCOUNT')) {
        categories.SITESALE.push(coupon);
      } else if (upperCoupon.includes('AFFILIATE') || upperCoupon.includes('PARTNER')) {
        categories.AFFILIATE.push(coupon);
      } else if (upperCoupon.includes('AMBASSADOR') || upperCoupon.includes('AMB')) {
        categories.AMBASSADOR.push(coupon);
      } else if (upperCoupon.includes('CAMPAIGN') || upperCoupon.includes('HOLD')) {
        categories.CAMPAIGN.push(coupon);
      } else if (upperCoupon.includes('CAREER') || upperCoupon.includes('EMAIL')) {
        categories.EMAILCAPTURE.push(coupon);
      } else if (upperCoupon.length <= 3 || upperCoupon.includes('COMMON')) {
        categories.COMMON.push(coupon);
      } else {
        categories.UNKNOWN.push(coupon);
      }
    });

    return categories;
  }

  /**
   * Gets a flat array of all unique coupon codes from all sources. This is the one place every
   * source is split and deduped, and it feeds the pricing calls and checkout URLs.
   */
  getAllCouponCodes(couponRequest: CouponCollectionRequest): string[] {
    const allCoupons: string[] = [];
    const { coupons } = couponRequest;

    // Add craft coupons
    if (coupons.craft?.length) {
      allCoupons.push(...coupons.craft);
    }

    // Add builder category coupons
    if (coupons.builderCategories?.length) {
      allCoupons.push(...coupons.builderCategories);
    }

    // Add global banner coupons
    if (coupons.globalBanner?.length) {
      allCoupons.push(...coupons.globalBanner);
    }

    // Capture coupons are stored as the raw Craft field value, which can be comma-separated;
    // pushed whole, "A,B,C" is one unknown code to the pricing API.
    allCoupons.push(...this.splitCouponList(coupons.emailCapture));
    allCoupons.push(...this.splitCouponList(coupons.phoneCapture));

    // Add URL parameter coupons (with comma-separated parsing)
    Object.values(coupons.urlParameters).forEach((coupon: string | undefined) => {
      allCoupons.push(...this.splitCouponList(coupon));
    });

    // Return unique coupons only
    const uniqueCoupons = [...new Set(allCoupons)];
    return uniqueCoupons;
  }
}
